import { sha256 } from "@noble/hashes/sha2.js";
import { randomBytes, toBase64URL, utf8 } from "./bytes";
import { demoGateway } from "./demo/gateway";
import { HermesError } from "./errors";
import { bool, obj, s, str, strings } from "./json";
import { relayHub } from "./relay/hub";
import type { RelayRoute } from "./relay/route";
import { ensureOK, httpError, jsonBody, parseJSON, withQuery, type HermesTransport, type HttpRequest, type HttpResponse } from "./transport";

/** 一台主机：经 TimiBot 中转（唯一的真实连接方式），或体验模式。 */
export type GatewayEndpoint = { kind: "relay"; relay: RelayRoute } | { kind: "demo" };

export const demoEndpoint: GatewayEndpoint = { kind: "demo" };

export function endpointTransport(ep: GatewayEndpoint): HermesTransport {
  return ep.kind === "relay" ? relayHub.tunnel(ep.relay) : demoGateway;
}

export function endpointKey(ep: GatewayEndpoint): string {
  return ep.kind === "relay" ? `relay:${ep.relay.keyID ?? ep.relay.hostID}` : "demo";
}

export function sameEndpoint(a: GatewayEndpoint | undefined, b: GatewayEndpoint | undefined): boolean {
  if (!a || !b) return a === b;
  if (a.kind !== b.kind) return false;
  if (a.kind === "demo") return true;
  const r = (b as { relay: RelayRoute }).relay;
  return a.relay.hostID === r.hostID && a.relay.keyID === r.keyID && a.relay.relayURL === r.relayURL;
}

export function displayHost(ep: GatewayEndpoint | undefined): string {
  if (!ep) return "";
  return ep.kind === "demo" ? "体验模式" : ep.relay.name;
}

// MARK: 凭据

export interface BearerTokens {
  access_token: string;
  refresh_token: string;
  /** Unix 秒 */
  expires_at?: number;
  provider: string;
  user_id?: string;
}

/**
 * 连接网关用的凭据。
 * - tunnel：经中转、网关没开门禁，connector 在本机替我们带上会话 token
 * - bearer：网关开了登录门禁，用 Hermes 原生登录换来的 token，每次连 WS 前换一张 ws-ticket
 */
export type GatewayCredential = { type: "tunnel" } | { type: "bearer"; tokens: BearerTokens } | { type: "sessionToken"; token: string };

export function credentialSummary(c: GatewayCredential): string {
  switch (c.type) {
    case "sessionToken": return "会话 Token";
    case "bearer": return `已登录（${c.tokens.provider}）`;
    case "tunnel": return "已配对（端到端加密）";
  }
}

/** 提前 60 秒视为过期，留出换 ticket 的时间 */
export function tokensExpired(t: BearerTokens, now = Date.now() / 1000): boolean {
  return t.expires_at !== undefined && now >= t.expires_at - 60;
}

function parseTokens(raw: unknown): BearerTokens {
  const o = obj(raw);
  const access = s(o?.access_token);
  if (!o || !access) throw HermesError.decoding("token 响应格式不正确");
  return {
    access_token: access,
    refresh_token: s(o.refresh_token) ?? "",
    expires_at: typeof o.expires_at === "number" ? o.expires_at : undefined,
    provider: s(o.provider) ?? "",
    user_id: s(o.user_id),
  };
}

/** /api/status 的公开字段 */
export interface ServerStatus {
  version?: string;
  release_date?: string;
  auth_required: boolean;
  auth_providers: string[];
  auth_flows: string[];
  gateway_running?: boolean;
  gateway_state?: string;
  active_sessions?: number;
  overall?: string;
}

export interface AuthProviderInfo {
  name: string;
  display_name: string;
  supports_password: boolean;
}

export function supportsNativeLogin(st: ServerStatus): boolean {
  return st.auth_flows.includes("native_pkce");
}

/**
 * 网关的 REST 鉴权接口（hermes_cli/dashboard_auth/routes.py），对应 HermesKit 的 GatewayAuthAPI。
 *
 * 用户名密码登录走 Hermes 官方的“原生应用授权”流程，全程 HTTP（经加密隧道），无需浏览器跳转：
 * 1. GET  /auth/native/authorize（PKCE challenge + 回环 redirect_uri）→ 网关在 cookie 里记下 broker 状态
 * 2. POST /auth/password-login（带着这个 cookie）→ 返回 next = 回环地址?code=…
 * 3. POST /auth/native/token（code + verifier）→ access/refresh token
 */
export class GatewayAuthAPI {
  constructor(readonly endpoint: GatewayEndpoint) {}

  private send(req: HttpRequest): Promise<HttpResponse> {
    return endpointTransport(this.endpoint).request(req);
  }

  async status(timeout = 10): Promise<ServerStatus> {
    const res = await this.send({ method: "GET", path: "/api/status", headers: { Accept: "application/json" }, timeout });
    ensureOK(res);
    const o = obj(parseJSON(res));
    if (!o) throw HermesError.decoding("这个地址看起来不是 Hermes 网关（/api/status 无法解析）");
    return {
      version: s(o.version),
      release_date: s(o.release_date),
      auth_required: bool(o.auth_required) ?? false,
      auth_providers: strings(o.auth_providers) ?? [],
      auth_flows: strings(o.auth_flows) ?? [],
      gateway_running: bool(o.gateway_running),
      gateway_state: s(o.gateway_state),
      active_sessions: typeof o.active_sessions === "number" ? o.active_sessions : undefined,
      overall: s(o.overall),
    };
  }

  async providers(): Promise<AuthProviderInfo[]> {
    const res = await this.send({ method: "GET", path: "/api/auth/providers", headers: { Accept: "application/json" } });
    ensureOK(res);
    const list = (obj(parseJSON(res))?.providers as unknown[]) ?? [];
    return list.map(obj).filter(Boolean).map((p) => ({
      name: s(p!.name) ?? "",
      display_name: s(p!.display_name) ?? s(p!.name) ?? "",
      supports_password: bool(p!.supports_password) ?? false,
    }));
  }

  async passwordLogin(provider: string, username: string, password: string): Promise<BearerTokens> {
    const verifier = toBase64URL(randomBytes(32));
    const challenge = toBase64URL(sha256(utf8(verifier)));
    const state = toBase64URL(randomBytes(16));
    const redirectURI = "http://127.0.0.1:53682/timibot/callback";

    // 1. authorize：只要它种下的 PKCE/broker cookie，不跟随 302
    const authRes = await this.send({
      method: "GET",
      path: withQuery("/auth/native/authorize", {
        provider, code_challenge: challenge, code_challenge_method: "S256", redirect_uri: redirectURI, state,
      }),
      timeout: 15,
    });
    if (authRes.status < 200 || authRes.status >= 400) throw httpError(authRes, "网关不支持原生登录");
    const cookie = cookieHeader(authRes.headers["set-cookie"]);

    // 2. 提交用户名密码
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (cookie) headers["Cookie"] = cookie;
    const loginRes = await this.send({
      method: "POST", path: "/auth/password-login", headers, timeout: 20,
      body: jsonBody({ provider, username, password, next: "" }),
    });
    switch (loginRes.status) {
      case 200: break;
      case 401: throw HermesError.authenticationRequired("用户名或密码不正确");
      case 429: throw HermesError.http(429, "尝试次数过多，请稍后再试");
      case 404: throw HermesError.http(404, `网关没有启用用户名密码登录（provider: ${provider}）`);
      default: throw httpError(loginRes, "登录失败");
    }
    const next = s(obj(parseJSON(loginRes))?.next);
    let code: string | null = null;
    let echoed: string | null = null;
    try {
      const u = new URL(next ?? "");
      code = u.searchParams.get("code");
      echoed = u.searchParams.get("state");
    } catch { /* 下面报错 */ }
    if (!code) throw HermesError.http(200, "网关没有返回授权码，可能是版本过旧（需要 Hermes ≥ 0.21）");
    if (echoed && echoed !== state) throw HermesError.authenticationRequired("登录状态校验失败，请重试");

    // 3. code + verifier 换 token
    return this.exchange("/auth/native/token", { code, code_verifier: verifier });
  }

  /** 轮换 refresh token。401 表示需要重新登录。 */
  async refresh(tokens: BearerTokens): Promise<BearerTokens> {
    if (!tokens.refresh_token) throw HermesError.authenticationRequired("登录已过期");
    try {
      return await this.exchange("/auth/native/refresh", { refresh_token: tokens.refresh_token, provider: tokens.provider });
    } catch (e) {
      if (e instanceof HermesError && e.kind === "http" && (e.status === 401 || e.status === 400)) {
        throw HermesError.authenticationRequired("登录已过期，请重新登录");
      }
      throw e;
    }
  }

  /** 用 Bearer token 换一张 30 秒单次有效的 WebSocket ticket。 */
  async mintWebSocketTicket(accessToken: string): Promise<string> {
    const res = await this.send({
      method: "POST", path: "/api/auth/ws-ticket", timeout: 15,
      headers: { Accept: "application/json", Authorization: `Bearer ${accessToken}` },
    });
    if (res.status === 401 || res.status === 403) throw HermesError.authenticationRequired("访问令牌已失效");
    ensureOK(res);
    const ticket = s(obj(parseJSON(res))?.ticket);
    if (!ticket) throw HermesError.decoding("ws-ticket 响应格式不正确");
    return ticket;
  }

  private async exchange(path: string, body: Record<string, string>): Promise<BearerTokens> {
    const res = await this.send({
      method: "POST", path, timeout: 15,
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: jsonBody(body),
    });
    ensureOK(res);
    return parseTokens(parseJSON(res));
  }
}

/**
 * 把响应里的 Set-Cookie 变成下一次请求的 Cookie 头（只取 name=value）。
 * 多个 Set-Cookie 被合并成逗号分隔的一行；Expires 里也有逗号，所以只在“逗号后紧跟 name=”处切分。
 */
export function cookieHeader(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const pairs = raw
    .replace(/,\s*(?=[^;,\s=]+=)/g, "\u001f")
    .split("\u001f")
    .map((c) => (c.split(";")[0] ?? "").trim())
    .filter((p) => p.includes("="));
  return pairs.length ? pairs.join("; ") : undefined;
}

// MARK: 凭据中介

/** 负责在每次建立 WebSocket 前准备好鉴权参数，并在 token 轮换后通知持久化层。 */
export class CredentialBroker {
  constructor(
    public endpoint: GatewayEndpoint,
    public credential: GatewayCredential | undefined,
    private readonly onUpdate: (c: GatewayCredential | undefined) => void = () => {},
  ) {}

  /** 本次连接要用的 WebSocket 路径。门禁模式下会先铸造 ws-ticket（必要时先刷新 token）。 */
  async webSocketPath(): Promise<string> {
    const c = this.credential;
    if (!c || c.type === "tunnel") return "/api/ws";
    if (c.type === "sessionToken") return withQuery("/api/ws", { token: c.token });
    const api = new GatewayAuthAPI(this.endpoint);
    let tokens = c.tokens;
    if (tokensExpired(tokens)) tokens = await this.refreshed(tokens, api);
    let ticket: string;
    try {
      ticket = await api.mintWebSocketTicket(tokens.access_token);
    } catch (e) {
      if (!(e instanceof HermesError && e.needsLogin)) throw e;
      // access token 被拒：强制刷新一次再试（与桌面端 mintGatewayWsTicket 一致）
      tokens = await this.refreshed(tokens, api);
      ticket = await api.mintWebSocketTicket(tokens.access_token);
    }
    return withQuery("/api/ws", { ticket });
  }

  private async refreshed(tokens: BearerTokens, api: GatewayAuthAPI): Promise<BearerTokens> {
    const fresh = await api.refresh(tokens);
    this.credential = { type: "bearer", tokens: fresh };
    this.onUpdate(this.credential);
    return fresh;
  }

  /** 带鉴权的 REST 请求（门禁模式用 Bearer，过期自动刷新一次；token 模式用 X-Hermes-Session-Token）。 */
  async authorized(req: HttpRequest): Promise<HttpResponse> {
    const transport = endpointTransport(this.endpoint);
    const api = new GatewayAuthAPI(this.endpoint);
    const c = this.credential;
    let tokens = c?.type === "bearer" ? c.tokens : undefined;
    const make = (): HttpRequest => {
      const headers = { ...(req.headers ?? {}) };
      if (c?.type === "sessionToken") headers["X-Hermes-Session-Token"] = c.token;
      if (tokens) headers["Authorization"] = `Bearer ${tokens.access_token}`;
      return { ...req, headers };
    };
    if (tokens && tokensExpired(tokens)) tokens = await this.refreshed(tokens, api);
    let res = await transport.request(make());
    if (res.status === 401 && tokens) {
      tokens = await this.refreshed(tokens, api);
      res = await transport.request(make());
    }
    return res;
  }

  async getJSON(path: string, query?: Record<string, string | undefined>): Promise<unknown> {
    const res = await this.authorized({ method: "GET", path: withQuery(path, query), headers: { Accept: "application/json" }, timeout: 20 });
    ensureOK(res);
    const json = parseJSON(res);
    if (json === undefined) throw HermesError.decoding(`${path} 返回的数据无法解析`);
    return json;
  }

  async sendJSON(method: string, path: string, body: unknown, query?: Record<string, string | undefined>): Promise<unknown> {
    const res = await this.authorized({
      method, path: withQuery(path, query), timeout: 30,
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: jsonBody(body),
    });
    ensureOK(res);
    return parseJSON(res) ?? null;
  }

  /** 带鉴权下载文件：返回内容和 MIME 类型。 */
  async download(path: string, query?: Record<string, string | undefined>): Promise<{ data: Uint8Array; mime?: string }> {
    const res = await this.authorized({ method: "GET", path: withQuery(path, query), headers: { Accept: "*/*" }, timeout: 300 });
    ensureOK(res);
    return { data: res.body, mime: res.headers["content-type"] };
  }
}

/** 登录页用的连通性检查：真的打开一次 /api/ws，等到 gateway.ready 为止。 */
export async function verifyGateway(endpoint: GatewayEndpoint, credential: GatewayCredential, timeout = 12): Promise<void> {
  const broker = new CredentialBroker(endpoint, credential);
  const path = await broker.webSocketPath();
  let socket;
  try {
    socket = await endpointTransport(endpoint).openSocket(path, timeout);
  } catch (e) {
    if (e instanceof HermesError && e.kind === "http" && (e.status === 403 || e.status === 401)) {
      throw HermesError.transport(`网关拒绝了 WebSocket（HTTP ${e.status}）。如果用了反向代理，请确认 dashboard.public_url 与访问域名一致`);
    }
    throw e;
  }
  const deadline = Date.now() + timeout * 1000;
  try {
    while (Date.now() < deadline) {
      const remaining = deadline - Date.now();
      const text = await Promise.race([
        socket.receive(),
        new Promise<never>((_, reject) => setTimeout(() => reject(HermesError.transport("连上了，但网关没有发送就绪信号")), remaining)),
      ]);
      let v: unknown;
      try { v = JSON.parse(text); } catch { continue; }
      const o = obj(v);
      if (str(o?.method) === "event" && str(obj(o?.params)?.type) === "gateway.ready") return;
    }
    throw HermesError.transport("连上了，但网关没有发送就绪信号");
  } finally {
    socket.close();
  }
}
